import { useEffect, useState } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Trash2, Plus, Save, Ticket, ArrowLeft, Users } from "lucide-react";
import { toast } from "sonner";

const money = (c) => new Intl.NumberFormat("en-US", { style: "currency", currency: "USD" }).format((c || 0) / 100);

/**
 * Concert manager — used by band owners to create a new concert (route
 * `/concerts/new`) or manage an existing one (route `/concerts/{cid}/manage`).
 * All state (form + tiers) is scoped to this page; on save we redirect
 * back to the public detail page.
 */
export default function ConcertManage() {
  const { cid } = useParams();  // undefined on /concerts/new
  const isNew = !cid;
  const navigate = useNavigate();
  const { user } = useAuth();
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [concert, setConcert] = useState(null);
  const [tiers, setTiers] = useState([]);
  const [form, setForm] = useState({
    title: "",
    description: "",
    venue_name: "",
    address: "",
    city: "",
    state: "",
    starts_at: "",
    cover_image_url: "",
    capacity: "",
    allow_rsvp: true,
    is_free: true,
  });

  useEffect(() => {
    if (isNew) return;
    (async () => {
      setLoading(true);
      try {
        const r = await api.get(`/concerts/${cid}`);
        const c = r.data.concert;
        setConcert(c);
        setTiers(r.data.tiers || []);
        setForm({
          title: c.title,
          description: c.description || "",
          venue_name: c.venue_name || "",
          address: c.address || "",
          city: c.city || "",
          state: c.state || "",
          starts_at: c.starts_at ? c.starts_at.slice(0, 16) : "",
          cover_image_url: c.cover_image_url || "",
          capacity: c.capacity || "",
          allow_rsvp: c.allow_rsvp,
          is_free: c.is_free,
        });
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || e.message);
      } finally { setLoading(false); }
    })();
  }, [cid, isNew]);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });

  const saveConcert = async () => {
    if (!form.title || !form.starts_at) {
      toast.error("Title and start time are required");
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        starts_at: new Date(form.starts_at).toISOString(),
        capacity: form.capacity ? parseInt(form.capacity, 10) : null,
      };
      if (isNew) {
        const r = await api.post("/concerts", payload);
        toast.success("Event created");
        navigate(`/concerts/${r.data.concert.id}/manage`);
      } else {
        await api.patch(`/concerts/${cid}`, payload);
        toast.success("Saved");
      }
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    } finally { setSaving(false); }
  };

  const cancelEvent = async () => {
    if (!window.confirm("Cancel this event? Attendees will no longer see it.")) return;
    try {
      await api.delete(`/concerts/${cid}`);
      toast("Event cancelled");
      navigate("/events");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const addTier = async () => {
    const name = window.prompt("Tier name (e.g. General Admission, VIP)");
    if (!name) return;
    const priceStr = window.prompt("Price in USD (e.g. 25 for $25, 0 for free)");
    const price = Math.max(0, Math.round(parseFloat(priceStr || "0") * 100));
    const qtyStr = window.prompt("How many available?");
    const qty = Math.max(1, parseInt(qtyStr || "0", 10));
    if (!qty) { toast.error("Quantity required"); return; }
    try {
      await api.post(`/concerts/${cid}/tiers`, {
        name, price_cents: price, qty_total: qty, sort_order: tiers.length,
      });
      toast.success("Tier added");
      const r = await api.get(`/concerts/${cid}`);
      setTiers(r.data.tiers || []);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  const deleteTier = async (tid) => {
    if (!window.confirm("Delete this tier?")) return;
    try {
      await api.delete(`/concerts/tiers/${tid}`);
      toast("Deleted");
      setTiers(tiers.filter((t) => t.id !== tid));
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || e.message);
    }
  };

  if (loading) return <div className="max-w-2xl mx-auto p-6 text-[#8B7B73]">Loading…</div>;

  return (
    <div className="max-w-3xl mx-auto px-6 md:px-10 py-8 pb-24" data-testid="concert-manage-page">
      <Link to={isNew ? "/events" : `/concerts/${cid}`} className="inline-flex items-center gap-1.5 text-sm text-[#B31E1E] hover:underline mb-4">
        <ArrowLeft size={14} /> Back
      </Link>
      <h1 className="font-display text-4xl tracking-tighter">
        {isNew ? "Create event" : "Manage event"}
      </h1>
      {!user && (
        <p className="mt-3 text-sm text-[#B31E1E]">Sign in as a band owner to continue.</p>
      )}

      <Card className="rounded-md p-6 mt-6 space-y-4" data-testid="concert-form">
        <div>
          <Label htmlFor="title">Title</Label>
          <Input id="title" required value={form.title} onChange={update("title")} className="rounded-sm mt-1" placeholder="Summer Concert Series — Night 1" data-testid="concert-title-input" />
        </div>
        <div>
          <Label htmlFor="description">Description</Label>
          <Textarea id="description" rows={4} value={form.description} onChange={update("description")} className="rounded-sm mt-1" placeholder="Lineup, doors, age restrictions, parking, what to bring…" data-testid="concert-description-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="starts_at">Starts at</Label>
            <Input id="starts_at" type="datetime-local" required value={form.starts_at} onChange={update("starts_at")} className="rounded-sm mt-1 font-mono" data-testid="concert-starts-input" />
          </div>
          <div>
            <Label htmlFor="capacity">Capacity (optional)</Label>
            <Input id="capacity" type="number" min={1} value={form.capacity} onChange={update("capacity")} className="rounded-sm mt-1 font-mono" placeholder="200" data-testid="concert-capacity-input" />
          </div>
        </div>
        <div>
          <Label htmlFor="venue_name">Venue</Label>
          <Input id="venue_name" value={form.venue_name} onChange={update("venue_name")} className="rounded-sm mt-1" placeholder="The Fillmore" data-testid="concert-venue-input" />
        </div>
        <div>
          <Label htmlFor="address">Street address</Label>
          <Input id="address" value={form.address} onChange={update("address")} className="rounded-sm mt-1" placeholder="1805 Geary Blvd" data-testid="concert-address-input" />
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="city">City</Label>
            <Input id="city" value={form.city} onChange={update("city")} className="rounded-sm mt-1" placeholder="San Francisco" data-testid="concert-city-input" />
          </div>
          <div>
            <Label htmlFor="state">State</Label>
            <Input id="state" maxLength={40} value={form.state} onChange={update("state")} className="rounded-sm mt-1" placeholder="CA" data-testid="concert-state-input" />
          </div>
        </div>
        <div>
          <Label htmlFor="cover_image_url">Cover image URL</Label>
          <Input id="cover_image_url" value={form.cover_image_url} onChange={update("cover_image_url")} className="rounded-sm mt-1" placeholder="https://…" data-testid="concert-cover-input" />
        </div>
        <label className="flex items-center gap-2 text-sm cursor-pointer">
          <input type="checkbox" checked={form.allow_rsvp} onChange={update("allow_rsvp")} data-testid="concert-allow-rsvp-toggle" />
          Allow RSVP (recommended for free events)
        </label>
        <div className="flex gap-2 pt-4">
          <Button onClick={saveConcert} disabled={saving} className="rounded-[35px] shadow-sm hover:shadow-md bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="concert-save-button">
            <Save size={14} className="mr-1.5" /> {saving ? "Saving…" : isNew ? "Create event" : "Save changes"}
          </Button>
          {!isNew && (
            <Button variant="outline" onClick={cancelEvent} className="rounded-[35px] border-red-300 text-red-600 hover:bg-red-50" data-testid="concert-cancel-event">
              <Trash2 size={14} className="mr-1.5" /> Cancel event
            </Button>
          )}
        </div>
      </Card>

      {/* Tiers panel — only after the concert exists */}
      {!isNew && (
        <Card className="rounded-md p-6 mt-6" data-testid="concert-tiers-panel">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h2 className="font-semibold text-lg">Ticket tiers</h2>
              <p className="text-xs text-[#5C4E48]">Add one tier for a single-price event, or multiple (GA + VIP) to differentiate.</p>
            </div>
            <Button onClick={addTier} size="sm" className="rounded-sm bg-[#B31E1E] hover:bg-[#8F1717]" data-testid="concert-add-tier-button">
              <Plus size={14} className="mr-1.5" /> Add tier
            </Button>
          </div>
          {tiers.length === 0 ? (
            <p className="text-sm text-[#5C4E48] py-4">
              No tiers yet — this event is currently free RSVP only.
            </p>
          ) : (
            <div className="space-y-2">
              {tiers.map((t) => (
                <div key={t.id} className="flex items-center justify-between border border-[#EAE3DE] rounded-md p-3" data-testid={`concert-tier-row-${t.id}`}>
                  <div>
                    <p className="font-semibold">{t.name}</p>
                    <p className="text-xs text-[#5C4E48]">
                      {money(t.price_cents)} · {t.qty_sold} / {t.qty_total} sold
                    </p>
                  </div>
                  <Button variant="ghost" size="icon" onClick={() => deleteTier(t.id)} className="text-red-600 hover:bg-red-50" data-testid={`concert-tier-delete-${t.id}`}>
                    <Trash2 size={15} />
                  </Button>
                </div>
              ))}
            </div>
          )}
          {concert && (
            <div className="mt-4 flex gap-3 text-xs text-[#5C4E48]">
              <span className="inline-flex items-center gap-1"><Ticket size={12} /> {concert.ticket_count || 0} tickets sold</span>
              <span className="inline-flex items-center gap-1"><Users size={12} /> {concert.rsvp_count || 0} RSVPs</span>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
